import React from 'react';
import { useNode } from '@craftjs/core';
import { css, cx } from '@emotion/css';

import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import {
  IMAGE_NODE_NAME,
  IMAGE_NODE_HEIGHT,
  DEFAULT_INTERACTION_EVENT,
  NO_LINK_EVENT,
} from '../../utils/constants';
import type { TRBLNumberCOORDS } from '../../types/layout';
import {
  getVisibilityClasses,
  DEFAULT_VISIBILITY,
  getBoxShadow,
  getPositionStyle,
  getPaddingStyle,
  getMarginStyle,
  getBorderRadiusStyle,
  getPositionPropsV2,
  getGlobalStyleColor,
  getMediaQueryHeader,
} from '../../utils/styleHelpers';
import { useRenderContext } from '../../context/RenderContext';
import type { StylesConfig } from '../../types/styles';
import { ImagePropsDto } from '../../dto/craft/props/ImagePropsDto';
import type { ImageContinuousType } from '../../types/animation';
import {
  getAnimationDataAttributes,
  getAnimationInitialClass,
  getHoverStyles,
} from '../../utils/animationHelpers';
import { getContinuousAnimationStyle } from '../../utils/continuousAnimationStyles';
import {
  getHrefAttr,
  getTargetAttr,
  getRelHrefAttr,
  getPopupTargetAttr,
} from '../../utils/attributesHelpers';

export type ImageProps = ImagePropsDto;

export const defaultProps: Partial<ImageProps> = {
  aspectRatioLock: false,
  aspectRatio: undefined,
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  zIndex: 0,
  backgroundSize: '100%',
  visibility: DEFAULT_VISIBILITY,
  src: '',
  alt: '',
  fitType: 'cover',
  backgroundColor: undefined,
  borderColor: undefined,
  hoverBorderColor: undefined,
  height: `${IMAGE_NODE_HEIGHT}px`,
  width: '100%',
  minWidth: 0,
  minHeight: 0,
  maxWidthUnit: 'px',
  maxWidth: 1200,
  flexBasis: '100%',
  padding: [0, 0, 0, 0],
  margin: [0, 0, 0, 0],
  borderRadius: [0, 0, 0, 0],
  position: 'relative',
  top: '0px',
  left: '0px',
  right: undefined,
  bottom: undefined,
  opacity: 1,
  borderWidth: 0,
  backgroundPosition: 'center top',
  objectPosition: 'center top',
  fillSpace: 'yes',
  flexGrow: 0,
  tablet: {},
  mobile: {
    width: '100%',
    fillSpace: 'no',
    flexBasis: '100%',
  },
};

function computeImageContentStyles(props: Partial<ImageProps>, activeGlobalStyles: StylesConfig) {
  const {
    padding,
    backgroundColor,
    borderColor,
    hoverBorderColor,
    borderWidth,
    borderRadius,
    shadowType,
    shadowSettings,
  } = props;

  // Convert arrays to CSS strings using utility functions
  const borderRadiusString = borderRadius
    ? getBorderRadiusStyle(borderRadius as TRBLNumberCOORDS).borderRadius
    : undefined;
  const paddingString = padding ? getPaddingStyle(padding as TRBLNumberCOORDS).padding : undefined;
  const borderWidthString = borderWidth ? `${borderWidth}px` : undefined;
  const background = getGlobalStyleColor(backgroundColor, activeGlobalStyles.colors);
  const borderColorString = getGlobalStyleColor(borderColor, activeGlobalStyles.colors);
  const hoverBorderColorString = getGlobalStyleColor(hoverBorderColor, activeGlobalStyles.colors);

  const hoverStylesObj = getHoverStyles(
    props.hoverEffect,
    props.hoverTransitionDuration,
    props.hoverIntensity,
    !!getBoxShadow(shadowType, shadowSettings),
  );
  const existingHover = hoverBorderColorString ? { borderColor: hoverBorderColorString } : {};
  const hoverEffectHover = (hoverStylesObj as Record<string, unknown>)['&:hover'] as
    | Record<string, unknown>
    | undefined;
  const mergedHover = { ...existingHover, ...hoverEffectHover };

  return {
    padding: paddingString,
    background,
    border:
      borderWidth && borderColorString
        ? `${borderWidthString} solid ${borderColorString}`
        : borderWidth
          ? `${borderWidthString} solid transparent`
          : undefined,
    borderRadius: borderRadiusString,
    ...(hoverStylesObj.transition ? { transition: hoverStylesObj.transition } : {}),
    ...(Object.keys(mergedHover).length > 0 && {
      '&:hover': mergedHover,
      boxShadow: getBoxShadow(shadowType, shadowSettings),
    }),
  };
}

const imageContentStyle = (
  props: Partial<ImageProps>,
  isServerRender: boolean,
  activeGlobalStyles: StylesConfig,
) => {
  const currentBpStyles = computeImageContentStyles(
    {
      ...props,
      padding: props.padding || [0, 0, 0, 0],
      borderRadius: props.borderRadius || [0, 0, 0, 0],
    },
    activeGlobalStyles,
  );
  const finalStyle: CSSInterpolation = {
    ...(currentBpStyles as CSSObject),
    position: 'relative',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    pointerEvents: 'auto',
  };
  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeImageContentStyles(mobile, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    const tabletStyles = computeImageContentStyles(tablet, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyle);
};

// Shared function that returns plain CSS properties for image node styles
const computeImageNodeStyles = (props: Partial<ImageProps>) => {
  const { height, width, maxWidth, maxWidthUnit, fillSpace, flexBasis, margin, zIndex } = props;
  const { position, top, left, right, bottom, positionAnchorsState } = props;
  const { aspectRatioLock, aspectRatio } = props;
  const positionStyle = getPositionStyle(position, top, left, right, bottom, positionAnchorsState);
  const marginString = margin ? getMarginStyle(margin).margin : undefined;

  const isAspectRatioLocked = aspectRatioLock && aspectRatio && aspectRatio > 0;
  const aspectRatioValue = aspectRatio ? `${aspectRatio} / 1` : undefined;

  // Use maxWidthUnit if provided, default to '%' for backward compatibility with existing data
  // New components will have maxWidthUnit: 'px' from defaultProps
  const unit = maxWidthUnit || '%';
  const maxWidthValue = maxWidth != null ? `${maxWidth}${unit}` : undefined;

  let flex = undefined;
  if (fillSpace) {
    if (fillSpace === 'yes') {
      if (flexBasis) {
        flex = `1 1 ${flexBasis}`;
      }
    } else {
      flex = 'unset';
    }
  }
  return {
    flex,
    padding: marginString,
    ...positionStyle,
    width: width || undefined,
    height: isAspectRatioLocked ? 'auto' : height || undefined,
    maxWidth: maxWidthValue,
    zIndex: zIndex || undefined,
    ...(isAspectRatioLocked && aspectRatioValue ? { aspectRatio: aspectRatioValue } : {}),
  };
};

const getImageNodeStyleClass = (props: Partial<ImageProps>, isServerRender: boolean) => {
  const { mobile, tablet, ...currentBpStyles } = getImageNodeStylesObject(props);

  const finalStyles: CSSInterpolation = {
    ...currentBpStyles,
  };
  if (mobile) {
    finalStyles[getMediaQueryHeader(isServerRender, 'mobile')] = mobile;
  }
  if (tablet) {
    finalStyles[getMediaQueryHeader(isServerRender, 'tablet')] = tablet;
  }

  return css(finalStyles);
};

function getImageNodeStylesObject(props: Partial<ImageProps>) {
  const currentBpStyles = computeImageNodeStyles({
    ...props,
    margin: props.margin || [0, 0, 0, 0],
  });
  const finalStyles = {
    ...currentBpStyles,
    display: 'flex',
    minHeight: 0, // this will make the image respect height when aspect ratio is locked
    mobile: {},
    tablet: {},
  };

  const { mobile, tablet } = props;
  if (mobile && Object.keys(mobile).length) {
    const positionProps = getPositionPropsV2(mobile as Partial<ImageProps>, props);
    const mobileStyles = computeImageNodeStyles({
      ...mobile,
      ...positionProps,
    } as Partial<ImageProps>);
    finalStyles.mobile = mobileStyles;
  }
  if (tablet && Object.keys(tablet).length) {
    const positionProps = getPositionPropsV2(tablet as Partial<ImageProps>, props);
    const tabletStyles = computeImageNodeStyles({
      ...tablet,
      ...positionProps,
    } as Partial<ImageProps>);
    finalStyles.tablet = tabletStyles;
  }

  return finalStyles;
}

const computeImageElementStyles = (props: Partial<ImageProps>) => {
  const {
    opacity,
    fitType,
    objectPosition,
    continuousAnimation,
    continuousSpeed,
    continuousIntensity,
  } = props;
  const continuousStyle = getContinuousAnimationStyle(
    continuousAnimation as ImageContinuousType | undefined,
    continuousSpeed,
    continuousIntensity,
  );
  return {
    opacity: opacity || undefined,
    objectFit: fitType ? (fitType !== 'tile' ? fitType : 'cover') : undefined,
    objectPosition: objectPosition ? objectPosition : undefined,
    display: fitType !== 'tile' ? 'block' : 'none',
    ...continuousStyle,
  };
};

const imageElementStyle = (props: Partial<ImageProps>, isServerRender: boolean) => {
  const currentBpStyles = computeImageElementStyles(props);
  const finalStyles: CSSInterpolation = {
    ...currentBpStyles,
    width: '100%',
    height: '100%',
  };
  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeImageElementStyles({
      ...mobile,
      fitType: props.fitType,
    } as Partial<ImageProps>);
    finalStyles[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles;
  }
  if (tablet) {
    const tabletStyles = computeImageElementStyles({
      ...tablet,
      fitType: props.fitType,
    } as Partial<ImageProps>);
    finalStyles[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles;
  }
  return css(finalStyles);
};
const computeImageTileStyles = (props: Partial<ImageProps>) => {
  const { src, backgroundSize, backgroundPosition, opacity, fitType } = props;
  return {
    backgroundImage: src ? `url(${src})` : undefined,
    backgroundSize: backgroundSize ? backgroundSize : undefined,
    backgroundPosition: backgroundPosition ? backgroundPosition : undefined,
    opacity: opacity || undefined,
    display: fitType === 'tile' ? 'block' : 'none',
  };
};

const imageTileStyle = (props: Partial<ImageProps>, isServerRender: boolean) => {
  const currentBpStyles = computeImageTileStyles(props);
  const finalStyles: CSSInterpolation = {
    ...currentBpStyles,
    position: 'absolute',
    top: '0px',
    left: '0px',
    right: '0px',
    bottom: '0px',
    pointerEvents: 'none',
    backgroundRepeat: 'repeat',
  };
  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeImageTileStyles({
      ...mobile,
      fitType: props.fitType,
    } as Partial<ImageProps>);
    finalStyles[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles;
  }
  if (tablet) {
    const tabletStyles = computeImageTileStyles({
      ...tablet,
      fitType: props.fitType,
    } as Partial<ImageProps>);
    finalStyles[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles;
  }
  return css(finalStyles);
};

const Image = (props: Partial<ImageProps> & { children?: React.ReactNode }) => {
  props = {
    ...defaultProps,
    ...props,
  };

  const { id } = useNode((node) => ({
    id: node.id,
  }));

  const { isEditable, environment, styles: activeGlobalStyles } = useRenderContext();

  const {
    src,
    alt,
    visibility = DEFAULT_VISIBILITY,
    children,
    clickEvent = DEFAULT_INTERACTION_EVENT,
  } = props;

  const visibilityClasses = getVisibilityClasses(visibility);

  const shouldWrapWithLink = clickEvent.eventType !== NO_LINK_EVENT;

  // Server-side rendering uses different styling approach
  const isServerRender = environment === 'server';
  const renderNodeWrapper = isServerRender || environment === 'inspiration-preview';

  const isBuilderMode = environment === 'builder';

  const animationClass = getAnimationInitialClass(props.animationType, environment);
  const animationAttrs = getAnimationDataAttributes(props);

  const imageElement = React.createElement(
    shouldWrapWithLink ? 'a' : 'div',
    {
      className: cx(
        'image-content',
        isEditable && 'node-dashed-indicator boundary-indicator styled-node',
        animationClass,
        imageContentStyle(props, isServerRender, activeGlobalStyles),
      ),
      ...animationAttrs,
      ...(shouldWrapWithLink
        ? {
            href: getHrefAttr(clickEvent),
            target: getTargetAttr(clickEvent),
            rel: getRelHrefAttr(clickEvent),
            'data-popup-target': getPopupTargetAttr(clickEvent),
            onClick: (e) => e.preventDefault(),
          }
        : {}),
      ...(isBuilderMode ? { 'data-animation-preview': id } : {}),
    },
    <>
      {!src && children}
      {src && <img src={src} alt={alt} className={imageElementStyle(props, isServerRender)} />}
      {/* Background image overlay for tiled images */}
      {src && <div className={imageTileStyle(props, isServerRender)} />}
    </>,
  );

  // Wrap with image-node div for server and inspiration-preview rendering
  // (in the editor, the Resizer component provides this wrapper instead)
  if (renderNodeWrapper) {
    // Return null if no src to match frontend behavior
    if (!src) return null;

    return (
      <div
        className={cx(
          'image-node',
          visibilityClasses,
          animationClass,
          getImageNodeStyleClass(props, isServerRender),
        )}
        {...animationAttrs}
      >
        {imageElement}
      </div>
    );
  }

  return imageElement;
};

Image.craft = {
  displayName: IMAGE_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: IMAGE_NODE_NAME,
    isLocked: false,
  },
};

export { getImageNodeStyleClass, computeImageNodeStyles, getImageNodeStylesObject, imageTileStyle };
export default Image;
